import { 
  Component,
  OnInit,
  Input,
  ChangeDetectionStrategy,
  ViewEncapsulation,
  ViewChild,
  Output,
  EventEmitter,
  OnDestroy,
  ChangeDetectorRef
} from '@angular/core';
import { Select } from '@ngxs/store';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { Observable, combineLatest, Subject } from 'rxjs';
import { ChapterModel } from 'src/app/story/models/chapter.model';
import { ViewService } from '../../view.service';
import { MediaModel } from 'src/app/story/models/media.model';
import { ViewAudioComponent } from '../view-audio/view-audio.component';
import { LayoutService } from '../../layout.service';
import { ViewLayout } from '../../models/view-layout.enum';
import { filter, takeUntil } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { EnvironmentApplication } from 'src/environments/models/environment-application';
import { ViewState } from '../../state/view.state';
import { ViewLayoutModel } from '../../models/view-layout.model';
import { StoryState } from 'src/app/story/state/story.state';
import { StoryModel } from 'src/app/story/models/story.model';
import { TextUtils } from 'src/app/shared/libraries/text-utils/text-utils';

@Component({
  selector: 'app-view-description',
  templateUrl: './view-description.component.html',
  styleUrls: ['./view-description.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class ViewDescriptionComponent implements OnInit, OnDestroy {  
  // ---------- Children ----------
  @ViewChild('audio', { static: false })
  audio: ViewAudioComponent;



  // ---------- Observables ----------
  @Select(StoryState.getStory)
  story$: Observable<StoryModel>;

  @Select(StoryChaptersState.getSelectedChapter)
  chapter$: Observable<ChapterModel>;

  @Select(StoryChaptersState.getSelectedChapterIndex)
  chapterIndex$: Observable<number>;

  @Select(StoryChaptersState.getChapterCount)
  chapterCount$: Observable<number>;

  @Select(StoryChaptersState.getSelectedChapterAudio)
  selectedChapterAudio$: Observable<MediaModel>;

  @Select(ViewState.getLayout)
  layout$: Observable<ViewLayoutModel>;


  
  // ---------- Class Variables ----------
  viewLayout = ViewLayout;
  ngUnsubscribe = new Subject();
  
  storyTitle = '';
  chapterTitle = '';
  chapterDescription = '';
  chapterAudio: MediaModel = null;


  
  // ---------- Input / Output ----------
  @Input()
  layout: 'drawer' | 'column' = 'drawer';

  @Input()
  public get isPopulated() {
    return this._isPopulated;
  }
  public set isPopulated(value) {
    this._isPopulated = value;
    this.isPopulatedChange.emit(this.isPopulated);
  }
  private _isPopulated = false;

  @Output()
  isPopulatedChange = new EventEmitter<boolean>();
  


  // ---------- Lifecycle ----------
  constructor(
    private viewService: ViewService,
    private layoutService: LayoutService,
    private changeDetectorRef: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.initContentListener();
    this.initLayoutListener();
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  isEnabled(layout: ViewLayout) {
    return this.layoutService.isLayoutEnabled(layout);
  }
  
  
  
  // ---------- Listeners ----------
  initContentListener() {
    combineLatest(
      this.story$,
      this.chapter$, 
      this.selectedChapterAudio$
    ).pipe(
      filter(([story, chapter, audio]) => story != null && chapter != null),
      takeUntil(this.ngUnsubscribe)
    ).subscribe(([story, chapter, audio]) => {
      // Only populate bookmark in web app
      this.storyTitle = (environment.application !== EnvironmentApplication.Viewer) ? TextUtils.normalize(story.displayName) : '';
      this.chapterTitle = TextUtils.normalize(chapter.info.displayName);
      this.chapterDescription = chapter.info.description;
      this.chapterAudio = audio;

      this.isPopulated = 
        !!this.storyTitle != null ||
        !!this.chapterTitle || 
        !!this.chapterDescription || 
        this.chapterAudio != null;
    });
  }

  initLayoutListener() {
    this.layout$.pipe(
      takeUntil(this.ngUnsubscribe)
    ).subscribe((layout) => {
      this.changeDetectorRef.markForCheck();
    });
  }


  // ---------- Interaction ----------
  handlePreviousChapterClick() {
    this.viewService.previousChapter();
  }

  handleNextChapterClick() {
    this.viewService.nextChapter();
  }

  handleAudioPlayClick() {
    if (this.audio.playing) {
      this.audio.pause();
    } else {
      this.audio.play();
    }
  }
}
